import React from "react";
import {Col, Form, Row} from "react-bootstrap";
import {format, isValid, parse} from "date-fns";
import DateInput from "../../../genericComponents/inputs/DateInput";
import {location, masks} from "../../../utils";
import {TERRAIN_CONDITIONS} from "./questionnaireDefinitions";
import {TERRAIN_FIELDS} from "./questionnaireEngine";

const MAX_TERRAINS = 5;

const onlyDigits = (value, max) => masks.removeNumberMask(value).slice(0, max);

function toDate(value) {
  if (!value) return null;
  const date = parse(value, "dd/MM/yyyy", new Date());
  return isValid(date) ? date : null;
}

function Label({question, required}) {
  return (
    <Form.Label>
      {question.label} {required ? <span className="is-required">*</span> : null}
    </Form.Label>
  );
}

function ErrorText({error}) {
  return error ? <div className="questionnaire-error">{error}</div> : null;
}

function RadioOptions({id, name, options, value, disabled, onChange}) {
  return (
    <div>
      {options.map((option) => (
        <Form.Check
          key={option}
          inline
          type="radio"
          id={`${id}-${option}`}
          name={id}
          label={option}
          checked={value === option}
          disabled={disabled}
          onChange={() => onChange(name, option)}
        />
      ))}
    </div>
  );
}

function SimpleInput({question, value, error, disabled, onChange}) {
  const {name, type} = question;
  const common = {
    id: `q-${name}`,
    value: value ?? "",
    disabled,
    isInvalid: Boolean(error),
    placeholder: question.placeholder,
    maxLength: question.maxLength,
  };
  switch (type) {
    case "textarea":
      return <Form.Control {...common} as="textarea" rows={3} onChange={(e) => onChange(name, e.target.value)}/>;
    case "password":
      return <Form.Control {...common} type="password" autoComplete="new-password"
                           onChange={(e) => onChange(name, e.target.value)}/>;
    case "year":
      return <Form.Control {...common} inputMode="numeric" placeholder="aaaa"
                           onChange={(e) => onChange(name, onlyDigits(e.target.value, 4))}/>;
    case "inss":
      return <Form.Control {...common} inputMode="numeric" placeholder={question.placeholder || "Número do benefício"}
                           onChange={(e) => onChange(name, onlyDigits(e.target.value, 12))}/>;
    case "select":
      return (
        <Form.Control {...common} as="select" onChange={(e) => onChange(name, e.target.value)}>
          <option value="">Selecione</option>
          {question.options.map((option) => <option key={option} value={option}>{option}</option>)}
        </Form.Control>
      );
    default:
      return <Form.Control {...common} onChange={(e) => onChange(name, e.target.value)}/>;
  }
}

/** Caixa de seleção que, marcada, abre um ou mais campos de texto (ex.: "Participa de cooperativa?"). */
function CheckText({id, question, answers, errors, checked, disabled, onToggle, onChange}) {
  return (
    <>
      <Form.Check
        type="checkbox"
        id={id}
        label={question.label}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onToggle(e.target.checked)}
      />
      {checked && (
        <Row>
          {question.fields.map((f) => (
            <Col key={f.name} xs={12} md={question.fields.length > 1 ? 6 : 12} className="mt-1">
              <Form.Control
                id={`q-${f.name}`}
                size="sm"
                value={answers[f.name] ?? ""}
                placeholder={f.placeholder}
                maxLength={f.maxLength}
                disabled={disabled}
                isInvalid={Boolean(errors[f.name])}
                onChange={(e) => onChange(f.name, e.target.value)}
              />
              <ErrorText error={errors[f.name]}/>
            </Col>
          ))}
        </Row>
      )}
    </>
  );
}

function Terrains({question, terrains, errors, disabled, onChange}) {
  const list = terrains || [];

  const setCount = (count) => {
    const next = list.slice(0, count);
    while (next.length < count) next.push(Object.fromEntries(TERRAIN_FIELDS.map((f) => [f, ""])));
    onChange(question.name, next);
  };
  const setField = (index, field, value) =>
    onChange(question.name, list.map((t, i) => {
      if (i !== index) return t;
      // trocar o estado invalida a cidade
      return field === "terrainState" ? {...t, terrainState: value, terrainCity: ""} : {...t, [field]: value};
    }));

  return (
    <>
      <Label question={question}/>
      <Form.Control as="select" value={list.length} disabled={disabled} className="questionnaire-terrain-count"
                    onChange={(e) => setCount(Number(e.target.value))}>
        <option value={0}>Nenhum</option>
        {Array.from({length: MAX_TERRAINS}, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
      </Form.Control>

      {list.map((terrain, i) => {
        const err = (field) => errors[`terrains.${i}.${field}`];
        const input = (field, label, props = {}) => (
          <Form.Group className="mb-2">
            <Form.Label>{label} <span className="is-required">*</span></Form.Label>
            <Form.Control id={`q-terrains.${i}.${field}`} value={terrain[field] ?? ""} disabled={disabled}
                          isInvalid={Boolean(err(field))} onChange={(e) => setField(i, field, e.target.value)} {...props}/>
            <ErrorText error={err(field)}/>
          </Form.Group>
        );
        return (
          <div key={i} className="questionnaire-terrain">
            <strong>Terreno {i + 1}</strong>
            <Form.Group className="mb-2" id={`q-terrains.${i}.condition`}>
              <Form.Label>Condição em relação ao imóvel? <span className="is-required">*</span></Form.Label>
              <RadioOptions id={`q-terrain-${i}-condition`} name="condition" options={TERRAIN_CONDITIONS}
                            value={terrain.condition} disabled={disabled}
                            onChange={(field, value) => setField(i, field, value)}/>
              <ErrorText error={err("condition")}/>
            </Form.Group>
            <Row>
              <Col xs={12} md={6}>
                {input("terrainState", "Estado onde o terreno se localiza", {
                  as: "select",
                  children: [
                    <option key="" value="">Selecione um estado</option>,
                    ...location.states.map((s) => <option key={s.value} value={s.value}>{s.label}</option>),
                  ],
                })}
              </Col>
              <Col xs={12} md={6}>
                {input("terrainCity", "Cidade do terreno", {
                  as: "select",
                  disabled: disabled || !terrain.terrainState,
                  children: [
                    <option key="" value="">Selecione uma cidade</option>,
                    ...(location.allCitiesFromStates[terrain.terrainState] || [])
                      .map((city) => <option key={city} value={city}>{city}</option>),
                  ],
                })}
              </Col>
              <Col xs={12} md={6}>{input("propertyArea", "Área total do imóvel", {maxLength: 20, inputMode: "decimal"})}</Col>
              <Col xs={12} md={6}>{input("exploredArea", "Área explorada", {maxLength: 20, inputMode: "decimal"})}</Col>
            </Row>
            {input("resources", "O que explora/explorava na atividade rural e destinação (milho, feijão, porcos, etc.)", {maxLength: 255})}
          </div>
        );
      })}
    </>
  );
}

/** Renderiza uma pergunta conforme o `type` (ver questionnaireDefinitions.js). */
function QuestionField({question, index, answers, errors, checkOpen, disabled, onChange, onToggleCheck}) {
  const required = typeof question.required === "function" ? question.required(answers) : question.required;
  const value = answers[question.name];
  const error = errors[question.name];
  const id = `q-${question.name || index}`;

  switch (question.type) {
    case "section":
      return <h6 className="questionnaire-section">{question.label}</h6>;

    case "radio":
      return (
        <Form.Group className="mb-3" id={id}>
          <Label question={question} required={required}/>
          <RadioOptions id={id} name={question.name} options={question.options} value={value}
                        disabled={disabled} onChange={onChange}/>
          <ErrorText error={error}/>
        </Form.Group>
      );

    case "checkboxes": {
      const selected = Array.isArray(value) ? value : [];
      const toggle = (option, checked) =>
        onChange(question.name, checked ? [...selected, option] : selected.filter((o) => o !== option));
      return (
        <Form.Group className="mb-3" id={id}>
          <Label question={question} required={required}/>
          <Row>
            {question.options.map((option) => (
              <Col key={option} xs={12} md={6} xl={4}>
                <Form.Check type="checkbox" id={`${id}-${option}`} label={option} disabled={disabled}
                            checked={selected.includes(option)} onChange={(e) => toggle(option, e.target.checked)}/>
              </Col>
            ))}
          </Row>
          <ErrorText error={error}/>
        </Form.Group>
      );
    }

    case "checkText":
      return (
        <Form.Group className="mb-3">
          <CheckText id={`q-check-${index}`} question={question} answers={answers} errors={errors}
                     checked={checkOpen} disabled={disabled} onChange={onChange}
                     onToggle={(checked) => onToggleCheck(index, checked)}/>
        </Form.Group>
      );

    case "terrains":
      return (
        <Form.Group className="mb-3">
          <Terrains question={question} terrains={value} errors={errors} disabled={disabled} onChange={onChange}/>
        </Form.Group>
      );

    case "date":
      return (
        <Form.Group className="mb-3" id={id}>
          <Label question={question} required={required}/>
          <DateInput
            name={question.name}
            value={toDate(value)}
            disabled={disabled}
            maxDate={new Date()}
            minDate={new Date(1900, 0, 1)}
            showYearDropdown
            dropdownMode="select"
            onChange={(name, date) => onChange(name, date ? format(date, "dd/MM/yyyy") : "")}
          />
          <ErrorText error={error}/>
        </Form.Group>
      );

    default:
      return (
        <Form.Group className="mb-3">
          <Label question={question} required={required}/>
          <SimpleInput question={question} value={value} error={error} disabled={disabled} onChange={onChange}/>
          <ErrorText error={error}/>
        </Form.Group>
      );
  }
}

export default React.memo(QuestionField);
